import { createStore } from 'vuex';
import session from './modules/session.js';

const store = createStore({
    state() {
        return {
            loading: false,
            projects: [],
            user: null,
            locale: import.meta.env.VITE_I18N_LOCALE,
            themeMode: 'dark',
            darkMode: true,
            installPrompt: null,
            installPromptEligible: false,
            serviceWorkerUpdating: false,
            passwordResetInfoMessage: '',
        };
    },

    mutations: {
        SET_PROJECTS(state, projects) {
            state.projects = projects;
        },
        SET_LOADING(state, loading) {
            state.loading = loading;
        },
        CLEAR_STORE(state) {
            state.user = null;
            state.projects = [];
        },
        SET_USER(state, user) {
            state.user = user
        },
        SET_LOCALE: (state, locale) => state.locale = locale,
        SET_THEME_MODE: (state, themeMode) => state.themeMode = themeMode,
        SET_DARK_MODE: (state, darkMode) => state.darkMode = darkMode,
        SET_INSTALL_PROMPT: (state, installPrompt) => state.installPrompt = installPrompt,
        SET_INSTALL_PROMPT_ELIGIBLE: (state, installPromptEligible) => state.installPromptEligible = installPromptEligible,
        SET_SERVICE_WORKER_UPDATING: (state, isUpdating) => state.serviceWorkerUpdating = isUpdating,
        SET_PASSWORD_RESET_INFO_MESSAGE: (state, message) => state.passwordResetInfoMessage = message,
    },

    actions: {
        setProjects({ commit }, projects) {
            commit('SET_PROJECTS', projects);
        },
        setLoading({ commit }, loading) {
            commit('SET_LOADING', loading);
        },
        clearStore({ commit, dispatch }) {
            commit('CLEAR_STORE');
            dispatch('session/clearSession');
        },
        setUser({ commit }, user) {
            commit('SET_USER', user);
        },
        setLocale: ({ commit }, locale) => commit('SET_LOCALE', locale),
        setThemeMode: ({ commit }, themeMode) => commit('SET_THEME_MODE', themeMode),
        setDarkMode: ({ commit }, darkMode) => commit('SET_DARK_MODE', darkMode),
        setInstallPrompt: ({ commit }, installPrompt) => commit('SET_INSTALL_PROMPT', installPrompt),
        setInstallPromptEligible: ({ commit }, installPromptEligible) => commit('SET_INSTALL_PROMPT_ELIGIBLE', installPromptEligible),
        setServiceWorkerUpdating: ({ commit }, isUpdating) => commit('SET_SERVICE_WORKER_UPDATING', isUpdating),
        setPasswordResetInfoMessage: ({ commit }, message) => commit('SET_PASSWORD_RESET_INFO_MESSAGE', message),
        resetPasswordResetInfoMessage: ({ commit }) => commit('SET_PASSWORD_RESET_INFO_MESSAGE', ''),
    },

    getters: {
        projects: state => state.projects,
        isLoading: state => state.loading,
        user: state => state.user,
        locale: state => state.locale,
        themeMode: state => state.themeMode,
        darkMode: state => state.darkMode,
        installPrompt: state => state.installPrompt,
        installPromptEligible: state => state.installPromptEligible,
        serviceWorkerUpdating: state => state.serviceWorkerUpdating,
        passwordResetInfoMessage: state => state.passwordResetInfoMessage,
    },
    modules: {
        session,
    },
});

store.subscribe((mutation, state) => {
    const partialState = {
        locale: state.locale,
        user: state.user,
        themeMode: state.themeMode,
        darkMode: state.darkMode,
        session: {
            accessToken: state.session.accessToken,
            accessTokenExpiresAt: state.session.accessTokenExpiresAt,
        },
        installPromptEligible: state.installPromptEligible,
    };

    localStorage.setItem('vuexState', JSON.stringify(partialState));
});

try {
    const savedState = localStorage.getItem('vuexState');
    if (savedState) {
        const partialState = JSON.parse(savedState);
        const restoredState = {};

        if (typeof partialState.locale === 'string') {
            restoredState.locale = partialState.locale;
        }
        if (['dark', 'light', 'system'].includes(partialState.themeMode)) {
            restoredState.themeMode = partialState.themeMode;
        } else if (typeof partialState.darkMode === 'boolean') {
            restoredState.themeMode = partialState.darkMode ? 'dark' : 'light';
        }
        if (typeof partialState.darkMode === 'boolean') {
            restoredState.darkMode = partialState.darkMode;
        }
        if (typeof partialState.installPromptEligible === 'boolean') {
            restoredState.installPromptEligible = partialState.installPromptEligible;
        }
        if (partialState.user !== null && typeof partialState.user === 'object' && !Array.isArray(partialState.user)) {
            restoredState.user = partialState.user;
        }

        const restoredSession = {
            ...store.state.session,
            ...partialState.session,
        };

        if (!restoredSession.accessToken && typeof partialState.accessToken === 'string' && partialState.accessToken.length > 0) {
            restoredSession.accessToken = partialState.accessToken;
        }

        store.replaceState({
            ...store.state,
            ...restoredState,
            session: restoredSession,
        });
    }
} catch {
    localStorage.removeItem('vuexState');
}

export default store;
